/**
 * The radius ladder, carried down the tree.
 *
 * No component in the kit takes a radius number from a pillar author. A
 * surface reads the rung its holder published and paints itself one rung
 * below, which is what makes a five-deep nest concentric without anyone doing
 * arithmetic at a call site — and what stops a 10px media frame resolving to a
 * 42.67px pill because a `Cbutton` defaulted to `RadiusSize.Rounded`.
 */
"use client";
import {
  createContext,
  useContext,
  type ReactElement,
  type ReactNode,
} from "react";
import { CRC_R_TRAY, radiusBelow } from "./cresultcard.tokens";

/** Nothing above the tray exists, so an unwrapped surface starts at its rung. */
const CresultcardRadiusContext = createContext<number>(CRC_R_TRAY);

interface CresultcardRadiusProviderProps {
  readonly children: ReactNode;
  /** The rung THIS surface paints. Children resolve one rung below it. */
  readonly radius: number;
}

export function CresultcardRadiusProvider({
  children,
  radius,
}: CresultcardRadiusProviderProps): ReactElement {
  return (
    <CresultcardRadiusContext.Provider value={radius}>
      {children}
    </CresultcardRadiusContext.Provider>
  );
}

/** The rung the holding surface paints. */
export function useCresultcardRadius(): number {
  return useContext(CresultcardRadiusContext);
}

/** The rung THIS surface should paint, given its holder's. */
export function useCresultcardNestedRadius(): number {
  return radiusBelow(useContext(CresultcardRadiusContext));
}
